import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import analyticsService, {
  CreatorOverview,
  TimeSeriesPoint,
  TopStream
} from '../services/analyticsService';
import './CreatorAnalytics.css';

const CreatorAnalytics: React.FC = () => {
  const [overview, setOverview] = useState<CreatorOverview | null>(null);
  const [playsData, setPlaysData] = useState<TimeSeriesPoint[]>([]);
  const [listenersData, setListenersData] = useState<TimeSeriesPoint[]>([]);
  const [topStreams, setTopStreams] = useState<TopStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  const getDateRange = (range: '7d' | '30d' | '90d'): { start: string; end: string } => {
    const end = new Date();
    const start = new Date();
    switch (range) {
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
    }
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { start, end } = getDateRange(dateRange);

      const [overviewData, playsResponse, listenersResponse, topStreamsData] = await Promise.all([
        analyticsService.getOverview(),
        analyticsService.getPlaysByDate(start, end),
        analyticsService.getListenerGrowth(start, end),
        analyticsService.getTopStreams(5)
      ]);

      setOverview(overviewData);
      setPlaysData(playsResponse.data);
      setListenersData(listenersResponse.data);
      setTopStreams(topStreamsData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load analytics';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleDateRangeChange = (range: '7d' | '30d' | '90d') => {
    if (range !== dateRange) {
      setDateRange(range);
    }
  };

  const getTotalPlays = (): number => {
    return playsData.reduce((sum, point) => sum + point.count, 0);
  };

  const getMaxValue = (data: TimeSeriesPoint[]): number => {
    return Math.max(...data.map(d => d.count), 1);
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={loadAnalytics} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <header className="page-header">
        <h1>Creator Analytics</h1>
        <p className="subtitle">Track your content performance</p>
      </header>

      {/* Overview Stats */}
      {overview && (
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-value">{overview.totalStreams}</span>
            <span className="stat-label">Total Streams</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{overview.totalPlays.toLocaleString()}</span>
            <span className="stat-label">Total Plays</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{overview.uniqueListeners}</span>
            <span className="stat-label">Unique Listeners</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{overview.avgPlaysPerStream}</span>
            <span className="stat-label">Avg Plays/Stream</span>
          </div>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="date-range-selector">
        <button
          className={`range-btn ${dateRange === '7d' ? 'active' : ''}`}
          onClick={() => handleDateRangeChange('7d')}
        >
          7 Days
        </button>
        <button
          className={`range-btn ${dateRange === '30d' ? 'active' : ''}`}
          onClick={() => handleDateRangeChange('30d')}
        >
          30 Days
        </button>
        <button
          className={`range-btn ${dateRange === '90d' ? 'active' : ''}`}
          onClick={() => handleDateRangeChange('90d')}
        >
          90 Days
        </button>
      </div>

      {/* Plays Chart */}
      <div className="chart-section">
        <div className="chart-header">
          <h2>Plays Over Time</h2>
          <span className="chart-total">{getTotalPlays().toLocaleString()} plays</span>
        </div>
        <div className="simple-chart">
          {playsData.length > 0 ? (
            <div className="bar-chart">
              {playsData.slice(-14).map((point, index) => (
                <div key={point.date} className="bar-container">
                  <div
                    className="bar"
                    style={{
                      height: `${(point.count / getMaxValue(playsData.slice(-14))) * 100}%`
                    }}
                    title={`${point.date}: ${point.count} plays`}
                  />
                  {index % 2 === 0 && (
                    <span className="bar-label">
                      {new Date(point.date).getDate()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">No play data available</div>
          )}
        </div>
      </div>

      {/* Listeners Chart */}
      <div className="chart-section">
        <div className="chart-header">
          <h2>Listener Growth</h2>
          <span className="chart-total">
            {listenersData.length > 0 ? listenersData[listenersData.length - 1].count : 0} total
          </span>
        </div>
        <div className="simple-chart">
          {listenersData.length > 0 ? (
            <div className="bar-chart">
              {listenersData.slice(-14).map((point, index) => (
                <div key={point.date} className="bar-container">
                  <div
                    className="bar listeners"
                    style={{
                      height: `${(point.count / getMaxValue(listenersData.slice(-14))) * 100}%`
                    }}
                    title={`${point.date}: ${point.count} listeners`}
                  />
                  {index % 2 === 0 && (
                    <span className="bar-label">
                      {new Date(point.date).getDate()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">No listener data available</div>
          )}
        </div>
      </div>

      {/* Top Streams */}
      <div className="top-streams-section">
        <h2>Top Performing Streams</h2>
        {topStreams.length === 0 ? (
          <div className="no-data">No streams yet</div>
        ) : (
          <div className="top-streams-list">
            {topStreams.map((stream, index) => (
              <Link
                key={stream.streamId}
                to={`/streams/${stream.streamId}`}
                className="top-stream-item"
              >
                <span className="analytics-stream-rank">{index + 1}</span>
                <div className="analytics-stream-cover">
                  {stream.coverImage ? (
                    <img src={stream.coverImage} alt={stream.name} />
                  ) : (
                    <div className="cover-placeholder">🎵</div>
                  )}
                </div>
                <div className="analytics-stream-info">
                  <h3 className="analytics-stream-name">{stream.name}</h3>
                  <span className="analytics-stream-plays">{stream.playCount.toLocaleString()} plays</span>
                </div>
                <div className={`analytics-stream-trend ${stream.trend >= 0 ? 'up' : 'down'}`}>
                  {stream.trend >= 0 ? '↑' : '↓'} {Math.abs(stream.trend)}%
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorAnalytics;
